
import { iniPaginationPTable, PaginationPTableType, PTableSimple } from "@/common/components";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { LocalPagination, TitleSizePage, NoData, Utilities } from "@/components/commons";
import { FilterOperator, ITransactionDeletionFilter } from "@/services";
import { IField, TransactionDeletion } from "@/entities";
import { i18n } from "@/i18n";
import VueFilters from "@/vue-filters";
import DateTimeMixin from "@/mixins/datetime";
import { customDebounce } from "@/common/utils";
import { PRow } from "@/common/components/look/PRow";
import PIcon from "@/common/components/look/PIcon/PIcon.vue";
import { PCol } from "@/common/components/look/PCol";

@Component({
  name: "transaction-deletions-grid",
  components: {
    PRow,
    PCol,
    PIcon,
    PTableSimple,
    LocalPagination,
    TitleSizePage,
    NoData,
  },
  mixins: [VueFilters, DateTimeMixin],
})
export class TransactionDeletionsGrid extends Vue {
  @Prop({ required: true }) filters!: ITransactionDeletionFilter;

  @Watch("filters", { immediate: true, deep: true })
  onChangeFilters(): void {
    this.pagination = { ...iniPaginationPTable };
    this.updatePaginationBack();
    this.mapFilterToRecord();
  }

  @Watch("params", { immediate: true, deep: true })
  onChangeParams(): void {
    if (this.firstLoad) {
      this.getItems();
      this.firstLoad = false;
    } else {
      customDebounce(() => this.getItems(), 350);
    }
  }

  @Watch("sort", { immediate: true, deep: true })
  onChangeSort(): void {
    if (
      this.paginationBack.sortWay != (this.sort.sortDesc ? "DESC" : "ASC") ||
      this.paginationBack.sortField != this.sort.sortField
    ) {
      this.paginationBack.sortWay = this.sort.sortDesc ? "DESC" : "ASC";
      this.paginationBack.sortField = this.sort.sortField;
      this.updateParams();
    }
  }

  paginate(): void {
    this.updatePaginationBack();
    this.updateParams();
  }

  translateStatus(status: string): string {
    const map: Record<string, string> = {
      PENDING: this.$t("delete_transactions_panel.status.PENDING") as string,
      PROCESSED: this.$t("delete_transactions_panel.status.PROCESSED") as string,
      PROCESSED_WITH_ERRORS: this.$t("delete_transactions_panel.status.PROCESSED_WITH_ERRORS") as string,
      ERROR: this.$t("delete_transactions_panel.status.ERROR") as string,
    };
    return map[status] || status;
  }

  mapFilterToRecord(): void {
    this.filters_field = [];
    if (!Utilities.isObjectEmpty(this.filters)) {
      const filtersAsRecord = this.filters as unknown as Record<string, unknown>;
      const keys = Object.keys(filtersAsRecord) as (keyof ITransactionDeletionFilter)[];
      keys.forEach((key) => {
        const mapped = this.mapFilters[key];
        const value = filtersAsRecord[key as string];
        if (!Utilities.isObjectEmpty(value) && mapped) {
          this.filters_field.push({
            field: mapped,
            operator: FilterOperator.FULL_TEXT_SEARCH,
            value: Array.isArray(value) ? value.join() : (value as unknown as string),
          });
        }
      });
      if (this.filters.startDate && this.filters.endDate) {
        this.filters_field.push({
          field: "requestDate",
          operator: FilterOperator.BETWEEN,
          value: `${this.filters.startDate.toISOString()},${this.filters.endDate.toISOString()}`,
        });
      }
    }
    this.updateParams();
  }

  updatePaginationBack(): void {
    this.paginationBack.limit = this.pagination.pageSize;
    this.paginationBack.offset = (this.pagination.currentPage - 1) * this.pagination.pageSize;
  }
  firstLoad = true;
  language = i18n.locale;
  sort: { sortDesc: boolean; sortField: string } = {
    sortDesc: true,
    sortField: "requestDate",
  };
  filters_field: Record<string, unknown>[] = [];
  pagination: PaginationPTableType = { ...iniPaginationPTable };
  params: Record<string, unknown> = {};
  mapFilters: Record<string, string> = {
    text: "fileName",
    status: "status",
  };
  items: TransactionDeletion[] = [];
  paginationDefault = {
    limit: 100,
  };
  paginationBack: { limit: number; offset: number; sortField: string; sortWay: string } = {
    limit: this.pagination.pageSize,
    offset: 0,
    sortField: this.sort.sortField,
    sortWay: this.sort.sortDesc ? "desc" : "asc",
  };

  updateParams(): void {
    const requestDateFrom =
      this.filters.startDate && this.filters.startDate.toISOString
        ? this.filters.startDate.toISOString()
        : undefined;
    const requestDateTo =
      this.filters.endDate && this.filters.endDate.toISOString
        ? this.filters.endDate.toISOString()
        : undefined;

    this.params = {
      fileName: this.filters.text,
      status: this.filters.status,
      requestDateFrom,
      requestDateTo,
      limit: this.paginationBack.limit,
      offset: (this.pagination.currentPage - 1) * this.pagination.pageSize,
      sortField: this.sort.sortField,
      sortWay: this.sort.sortDesc ? "desc" : "asc",
    };
  }

  async getItems(): Promise<void> {
    const data = await this.$services.transactionDeletion.fetchTransactionDeletions(this.params);
    this.items = data.results;
    this.pagination.totalElements = data.totalResult;
  }

  get fields(): IField[] {
    return [
      {
        key: "fileName",
        label: this.$t("support_panel.table.filename") as string,
        sortable: true,
        class: "",
        thClass: "table-header",
        tdClass: "table-cell table-cell-middle",
        visible: true,
        aux: false,
        code: "",
      },
      {
        key: "totalTransactions",
        label: this.$t("support_panel.table.totalTransactions") as string,
        sortable: true,
        class: "",
        thClass: "table-header",
        tdClass: "table-cell table-cell-middle",
        visible: true,
        aux: false,
        code: "",
      },
      {
        key: "status",
        label: this.$t("support_panel.table.status") as string,
        sortable: true,
        class: "",
        thClass: "table-header",
        tdClass: "table-cell table-cell-middle",
        visible: true,
        aux: false,
        code: "",
      },
      {
        key: "requestDate",
        label:
          (this.$t("delete_transactions_panel.table.transactionDate") as string) ||
          "Fecha de transacción",
        sortable: true,
        class: "",
        thClass: "table-header",
        tdClass: "table-cell table-cell-middle",
        visible: true,
        aux: false,
        code: "",
      },
      {
        key: "requestedByUserEmail",
        label: this.$t("support_panel.table.userEmail") as string,
        sortable: true,
        class: "",
        thClass: "table-header",
        tdClass: "table-cell table-cell-middle",
        visible: true,
        aux: false,
        code: "",
      },
      {
        key: "actions",
        label: this.$t("support_panel.table.download") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell table-cell-download",
        visible: true,
        aux: true,
      },
    ];
  }

  async downloadDetails(id: string): Promise<void> {
    const file = await this.$services.transactionDeletion.downloadDetailsById(id);
    const aElement = document.createElement("a");
    const url = URL.createObjectURL(file);
    aElement.href = url;
    aElement.download = file.name;
    aElement.click();
    window.URL.revokeObjectURL(url);
  }

  canDownload(status: string): boolean {
    return status === "PROCESSED" || status === "PROCESSED_WITH_ERRORS";
  }

  onDownload(item: TransactionDeletion): void {
    if (!this.canDownload(item.status)) {
      return;
    }
    void this.downloadDetails(item.id);
  }
}

export default TransactionDeletionsGrid;
